import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNpsAccess } from './useNpsAccess';
import {
  DEV2_DEVICE_TYPE_OPTIONS,
  DEV2_ONX_BUBBLE_ID,
  DEV2_TRIGGER_STEPS,
  defaultDev2DeviceTypeForParent,
  defaultBubbleIdForDeviceType,
  dev2TwilioToForDeviceType,
  dev2TestingDateForSchedule,
  DEV2_TESTING_DATE_TIMEZONE,
} from './dev2DeviceTestConfig';
import {
  FP_API_BASE,
  DEV2_DEVICE_TEST_TRIGGER_PATH,
  DEV2_DEVICE_TEST_SMS_REPLY_PATH,
  buildDev2TriggerPayload,
  sendDev2DeviceTestSmsReply,
  triggerDev2DeviceTest,
} from './dev2DeviceTestApi';

function NavigateToHub() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/global-search', { replace: true });
  }, [navigate]);
  return null;
}

function formatStepData(data) {
  if (data === undefined || data === null) return '—';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function stepStatusColor(status) {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400) return 'error';
  return 'default';
}

const Dev2TestingPage = ({ searchContext }) => {
  const { allowed, checked } = useNpsAccess();
  const prefill = searchContext?.dev2TestPrefill;

  const [accountNumber, setAccountNumber] = useState('');
  const [deviceType, setDeviceType] = useState('onx');
  const [bubbleId, setBubbleId] = useState(defaultBubbleIdForDeviceType('onx'));
  const [from, setFrom] = useState('');
  const [firstName, setFirstName] = useState('Ops');
  const [lastName, setLastName] = useState('Test');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [busyKey, setBusyKey] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [copyOk, setCopyOk] = useState(false);
  const [error, setError] = useState(null);

  const twilioTo = useMemo(() => dev2TwilioToForDeviceType(deviceType), [deviceType]);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.accountNumber) setAccountNumber(prefill.accountNumber);
    if (prefill.from) setFrom(prefill.from);
    if (prefill.firstName) setFirstName(prefill.firstName);
    if (prefill.lastName) setLastName(prefill.lastName);
    const inferred = defaultDev2DeviceTypeForParent(prefill.parent);
    setDeviceType(inferred);
    setBubbleId(defaultBubbleIdForDeviceType(inferred));
  }, [prefill]);

  const handleDeviceTypeChange = (value) => {
    setDeviceType(value);
    setBubbleId(defaultBubbleIdForDeviceType(value));
  };

  const buildTwilioRoutingBody = useCallback(
    () => ({
      accountNumber: accountNumber.trim(),
      deviceType,
      from: from.trim(),
      to: twilioTo,
    }),
    [accountNumber, deviceType, from, twilioTo]
  );

  /** Inbound SOS — no device bubbleId. */
  const buildSosBody = useCallback(() => {
    const body = { ...buildTwilioRoutingBody() };
    const lat = String(latitude).trim();
    const lng = String(longitude).trim();
    if (lat !== '' && !Number.isNaN(Number(lat))) body.latitude = Number(lat);
    if (lng !== '' && !Number.isNaN(Number(lng))) body.longitude = Number(lng);
    return body;
  }, [buildTwilioRoutingBody, latitude, longitude]);

  /** Schedule test window — device bubbleId identifies which device is under test. */
  const buildScheduleBody = useCallback(
    () => ({
      accountNumber: accountNumber.trim(),
      deviceType,
      bubbleId: bubbleId.trim(),
      testingDate: dev2TestingDateForSchedule(),
      from: from.trim(),
      to: twilioTo,
      firstName: firstName.trim() || 'Ops',
      lastName: lastName.trim() || 'Test',
    }),
    [accountNumber, deviceType, bubbleId, from, twilioTo, firstName, lastName]
  );

  const buildValidateBody = useCallback(
    () => ({
      accountNumber: accountNumber.trim(),
      deviceType,
    }),
    [accountNumber, deviceType]
  );

  /** Full pipeline: schedule + SOS fields; bubbleId is only for the schedule hop. */
  const buildFullTestBody = useCallback(
    () => ({
      ...buildScheduleBody(),
      ...buildSosBody(),
    }),
    [buildScheduleBody, buildSosBody]
  );

  const runApi = async (key, requestRecord, runner) => {
    setError(null);
    setLastResult(null);
    setCopyOk(false);
    setLastRequest(requestRecord);
    setBusyKey(key);
    try {
      const result = await runner();
      setLastResult(result);
      if (!result.ok) {
        setError(result.message || 'Request did not complete successfully.');
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setBusyKey(null);
    }
  };

  const requireAccount = () => {
    if (!accountNumber.trim()) {
      setError('Account number is required.');
      return false;
    }
    return true;
  };

  const requireBubble = () => {
    if (!bubbleId.trim()) {
      setError(
        deviceType === 'onx'
          ? 'Device bubbleId is required (onX is usually onX App).'
          : 'Enter the device bubbleId for the OXR hardware you are testing.'
      );
      return false;
    }
    return true;
  };

  const requireTwilio = () => {
    if (!requireAccount()) return false;
    if (!from.trim()) {
      setError('Twilio From (member phone) is required.');
      return false;
    }
    return true;
  };

  const makeRequestRecord = (path, jsonBody) => ({
    method: 'POST',
    url: `${FP_API_BASE}${path}`,
    jsonBody,
  });

  const runFullTest = () => {
    if (!requireBubble() || !requireTwilio()) return;
    const body = buildFullTestBody();
    const jsonBody = buildDev2TriggerPayload(body);
    runApi('full', makeRequestRecord(DEV2_DEVICE_TEST_TRIGGER_PATH, jsonBody), () =>
      triggerDev2DeviceTest(body)
    );
  };

  const runSchedule = () => {
    if (!requireBubble() || !requireAccount()) return;
    const body = buildScheduleBody();
    const jsonBody = buildDev2TriggerPayload(body, { step: DEV2_TRIGGER_STEPS.schedule });
    runApi('schedule', makeRequestRecord(DEV2_DEVICE_TEST_TRIGGER_PATH, jsonBody), () =>
      triggerDev2DeviceTest(body, { step: DEV2_TRIGGER_STEPS.schedule })
    );
  };

  const runValidate = () => {
    if (!requireAccount()) return;
    const body = buildValidateBody();
    const jsonBody = buildDev2TriggerPayload(body, { step: DEV2_TRIGGER_STEPS.validate });
    runApi('validate', makeRequestRecord(DEV2_DEVICE_TEST_TRIGGER_PATH, jsonBody), () =>
      triggerDev2DeviceTest(body, { step: DEV2_TRIGGER_STEPS.validate })
    );
  };

  const runSos = () => {
    if (!requireTwilio()) return;
    const body = buildSosBody();
    const jsonBody = buildDev2TriggerPayload(body, { step: DEV2_TRIGGER_STEPS.sos });
    runApi('sos', makeRequestRecord(DEV2_DEVICE_TEST_TRIGGER_PATH, jsonBody), () =>
      triggerDev2DeviceTest(body, { step: DEV2_TRIGGER_STEPS.sos })
    );
  };

  const runSmsReply = (reply) => {
    if (!requireTwilio()) return;
    const key = reply === '1' ? 'sms-pass' : 'sms-deviation';
    const jsonBody = { ...buildTwilioRoutingBody(), reply };
    runApi(key, makeRequestRecord(DEV2_DEVICE_TEST_SMS_REPLY_PATH, jsonBody), () =>
      sendDev2DeviceTestSmsReply(jsonBody)
    );
  };

  const lastRequestText = useMemo(() => {
    if (!lastRequest) return '';
    return JSON.stringify(lastRequest, null, 2);
  }, [lastRequest]);

  const handleCopyRequest = async () => {
    if (!lastRequestText) return;
    try {
      await navigator.clipboard.writeText(lastRequestText);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setError('Could not copy request to clipboard');
    }
  };

  const buttonBusy = (key) => busyKey === key;

  const bubbleHelperText =
    deviceType === 'onx'
      ? `onX device bubbleId is usually "${DEV2_ONX_BUBBLE_ID}".`
      : 'OXR: enter the device bubbleId for the unit you are testing on Dev2.';

  const validateStepHint = useMemo(() => {
    const steps = lastResult?.steps;
    if (!Array.isArray(steps)) return null;

    const schedule = steps.find((s) => s.step === 'schedule');
    if (schedule?.status != null && schedule.status >= 400) {
      const detail =
        typeof schedule.data === 'string'
          ? schedule.data
          : schedule.data?.message || schedule.data?.reason;
      return `Schedule failed (HTTP ${schedule.status})${detail ? `: ${detail}` : ''}. Validate will not see an active window until scheduling succeeds — fix schedule first (account, device bubbleId, testingDate).`;
    }

    const validate = steps.find((s) => s.step === 'validate');
    if (!validate) return null;
    const data = validate.data;
    if (data && typeof data === 'object' && data.testDevice === false) {
      const reason = data.reason ? ` (${data.reason})` : '';
      return `Validate returned testDevice: false${reason}. If schedule did not return HTTP 200, there is no window. Otherwise confirm account, device bubbleId, and that validate runs while the window is still open.`;
    }
    return null;
  }, [lastResult]);

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <NavigateToHub />;
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 3, px: 2 }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" component="h1">
            Dev2 device test (Ops)
          </Typography>
          <Chip label="Dev2 only" size="small" color="warning" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Schedules a test window on Dev2, validates, and simulates inbound Twilio traffic. Each action
          below is independent. Tests always schedule for <strong>right now</strong>.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Schedule</strong> uses the device <strong>bubbleId</strong> for the unit under test.
          <strong> SOS / SMS</strong> send account number (where applicable), product, and Twilio From/To — not
          bubbleId. Dev2 inbound <strong>To</strong> is fixed per product. Schedule sends{' '}
          <strong>testingDate</strong> as {DEV2_TESTING_DATE_TIMEZONE} (ISO without <code>Z</code>) on
          schedule / full test. The exact JSON sent is shown under <strong>Request payload</strong> after
          each action.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {lastResult && (
          <Alert severity={lastResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {lastResult.message || (lastResult.ok ? 'Completed.' : 'Request finished with errors.')}
            {lastResult.httpStatus && lastResult.httpStatus !== 200 && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                HTTP {lastResult.httpStatus}
              </Typography>
            )}
          </Alert>
        )}

        {validateStepHint && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {validateStepHint}
          </Alert>
        )}

        {lastRequest && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2">Request payload</Typography>
              <IconButton
                size="small"
                onClick={handleCopyRequest}
                aria-label="Copy request payload"
                title="Copy for backend"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Stack>
            {copyOk && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mb: 0.5 }}>
                Copied to clipboard
              </Typography>
            )}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {lastRequestText}
              </Typography>
            </Paper>
          </Box>
        )}

        <Stack spacing={2}>
          <TextField
            label="Account number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            fullWidth
            required
            helperText="Required for SOS/SMS (and schedule); identifies the member on Dev2"
          />

          <FormControl fullWidth required>
            <InputLabel id="dev2-device-type-label">Product</InputLabel>
            <Select
              labelId="dev2-device-type-label"
              label="Product"
              value={deviceType}
              onChange={(e) => handleDeviceTypeChange(e.target.value)}
            >
              {DEV2_DEVICE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Device bubbleId"
            value={bubbleId}
            onChange={(e) => setBubbleId(e.target.value)}
            fullWidth
            required
            placeholder={deviceType === 'onx' ? DEV2_ONX_BUBBLE_ID : 'Device bubbleId on Dev2'}
            helperText={`${bubbleHelperText} Used only when scheduling a test (not sent on SOS or SMS).`}
          />

          <TextField
            label="Twilio From (member phone)"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            fullWidth
            placeholder="+15551234567"
            helperText="E.164 — member phone on the device"
          />

          <TextField
            label="Twilio To (Dev2 inbound)"
            value={twilioTo}
            fullWidth
            disabled
            helperText="Locked — onX and OXR each use a fixed Dev2 number"
          />

          <Accordion
            expanded={advancedOpen}
            onChange={(_, exp) => setAdvancedOpen(exp)}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Advanced (schedule names & SOS coordinates)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="First name (schedule)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Last name (schedule)"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Latitude (SOS)"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                    fullWidth
                    placeholder="Server default if empty"
                  />
                  <TextField
                    label="Longitude (SOS)"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                    fullWidth
                    placeholder="Server default if empty"
                  />
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Typography variant="subtitle2">Actions</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Only the button you clicked shows a spinner.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={runFullTest}
            disabled={buttonBusy('full')}
            startIcon={buttonBusy('full') ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Run full test (schedule + validate + SOS)
          </Button>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              onClick={runSchedule}
              disabled={buttonBusy('schedule')}
              startIcon={
                buttonBusy('schedule') ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Schedule window (now)
            </Button>
            <Button
              variant="outlined"
              onClick={runValidate}
              disabled={buttonBusy('validate')}
              startIcon={
                buttonBusy('validate') ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Validate test device
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={runSos}
              disabled={buttonBusy('sos')}
              startIcon={buttonBusy('sos') ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Send inbound SOS
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              onClick={() => runSmsReply('1')}
              disabled={buttonBusy('sms-pass')}
              startIcon={
                buttonBusy('sms-pass') ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              SMS reply: Pass (1)
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => runSmsReply('2')}
              disabled={buttonBusy('sms-deviation')}
              startIcon={
                buttonBusy('sms-deviation') ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              SMS reply: Deviation (2)
            </Button>
          </Stack>
        </Stack>

        {Array.isArray(lastResult?.steps) && lastResult.steps.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Steps
            </Typography>
            <Stack spacing={1}>
              {lastResult.steps.map((step, index) => (
                <Paper key={`${step.step}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {step.step || `step ${index + 1}`}
                    </Typography>
                    {step.status != null && (
                      <Chip
                        size="small"
                        label={`HTTP ${step.status}`}
                        color={stepStatusColor(step.status)}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                    }}
                  >
                    {formatStepData(step.data)}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Dev2TestingPage;
